import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ChannelStatusAction,
  useGetClinicChannelStatusesQuery,
  WidgetRequestType,
  useGetClinicUsersQuery,
  GetClinicFormTemplatesQuery,
  ConnectSettingsClinicWidgetRequestTypeFragment,
  WidgetVisibility,
} from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';
import { WidgetRequestTypeIconNames } from 'shared/utils/widgetRequests';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import useForms from 'pages/Forms/hooks/useForms';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import sortBy from 'lodash-es/sortBy';
import { WidgetRequestTypeLabels } from '@televet/shared-types/widgetRequest/WidgetRequestTypeLabels';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsRequestWidgetEvents } from 'shared/providers/TrackingProvider/EventNames';
import { ISelectInputOption } from 'shared/components/SelectInput';
import { RequestTypeActions } from './RequestTypeDraggableRow';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu/components/MenuItem';
import { Box, HStack, VStack, Wrap } from '@televet/kibble-ui/build/chakra';
import { Select } from '@televet/kibble-ui/build/components/Select';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { RadioGroup } from '@televet/kibble-ui/build/components/RadioGroup';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Menu } from '@televet/kibble-ui/build/components/Menu';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Icon } from '@televet/kibble-ui/build/components/Icon';

export type RequestTypesModalFormValues = {
  requestTypeId?: string;
  requestType: WidgetRequestType;
  displayName: string;
  channelStatusId: string;
  requestTypeAction: string;
  assigneeIds?: string[];
  formTemplateId?: string;
  requestVisibility: WidgetVisibility;
};

const WidgetVisibilityOptions = [
  {
    children: <Text>When the clinic is open</Text>,
    value: WidgetVisibility.ClinicOpen,
  },
  {
    children: <Text>When the clinic is closed</Text>,
    value: WidgetVisibility.ClinicClosed,
  },
  {
    children: <Text>Always</Text>,
    value: WidgetVisibility.Always,
  },
];

const widgetRequestTypeOptions: ISelectInputOption[] = [
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.RequestAppointment],
    value: WidgetRequestType.RequestAppointment,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.RequestBoarding],
    value: WidgetRequestType.RequestBoarding,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.RequestGroomingAppoinment],
    value: WidgetRequestType.RequestGroomingAppoinment,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.RequestRxRefill],
    value: WidgetRequestType.RequestRxRefill,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.AdvancedRequestRxRefill],
    value: WidgetRequestType.AdvancedRequestRxRefill,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.RequestMedicalRecords],
    value: WidgetRequestType.RequestMedicalRecords,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.RequestVirtualConsult],
    value: WidgetRequestType.RequestVirtualConsult,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.TalkToStaff],
    value: WidgetRequestType.TalkToStaff,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.InboundSms],
    value: WidgetRequestType.InboundSms,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.DirectBooking],
    value: WidgetRequestType.DirectBooking,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.Teletriage],
    value: WidgetRequestType.Teletriage,
  },
  {
    label: WidgetRequestTypeLabels[WidgetRequestType.LiveAgent],
    value: WidgetRequestType.LiveAgent,
  },
];

interface IRequestTypesModalProps {
  isOpen: boolean;
  clinicWidgetRequestType?: ConnectSettingsClinicWidgetRequestTypeFragment;
  isLoading: boolean;
  onSubmit: (values: RequestTypesModalFormValues) => void;
  onCancel: () => void;
}

type FormTemplateOption = {
  label: string;
  value: string;
};

const RequestTypesModal = ({
  isOpen,
  clinicWidgetRequestType,
  isLoading,
  onSubmit: onSubmitProp,
  onCancel,
}: IRequestTypesModalProps): JSX.Element => {
  const {
    id: requestTypeId,
    requestType,
    displayName,
    channelStatus,
    formTemplate,
    assignees: clinicUserAssignees,
  } = clinicWidgetRequestType || {};

  const requestTypeAction = !!clinicWidgetRequestType ? RequestTypeActions.Edit : RequestTypeActions.Add;

  const { currentClinicId } = useClinicUser();
  const { isFeatureEnabled } = useFeatureFlag();
  const { trackEvent } = useTracking();
  const { hasPrescriptionCapabilities } = useIntegrationsProvider();
  const [formTemplates, setFormTemplates] = useState<FormTemplateOption[]>([]);

  // Consolidated form state
  const [form, setForm] = useState({
    requestType: requestType || WidgetRequestType.RequestAppointment,
    displayName: displayName || '',
    channelStatusId: channelStatus?.id || '',
    assigneeIds: clinicUserAssignees?.map((assignee) => assignee.id) || [],
    formTemplateId: formTemplate?.id || '',
    requestVisibility: WidgetVisibility.ClinicOpen,
  });

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const { data: channelStatusesData } = useGetClinicChannelStatusesQuery({
    variables: { clinicId: currentClinicId || '' },
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    skip: !currentClinicId,
  });

  const channelStatusOptions = useMemo(() => {
    if (!channelStatusesData?.findManyChannelStatus) return [];
    return channelStatusesData.findManyChannelStatus
      .filter((status) => status.channelStatusAction === ChannelStatusAction.Active)
      .map((status) => ({ label: status.name, value: status.id }));
  }, [channelStatusesData]);

  useEffect(() => {
    setForm({
      requestType: clinicWidgetRequestType?.requestType || widgetRequestTypeOptions[0]?.value,
      displayName: clinicWidgetRequestType?.displayName || '',
      channelStatusId: clinicWidgetRequestType?.channelStatus?.id || channelStatusOptions[0]?.value || '',
      assigneeIds: clinicWidgetRequestType?.assignees?.map((assignee) => assignee.id) || [],
      formTemplateId: clinicWidgetRequestType?.formTemplate?.id || formTemplates[0]?.value || '',
      requestVisibility: clinicWidgetRequestType?.requestVisibility || WidgetVisibility.Always,
    });
  }, [clinicWidgetRequestType, channelStatusOptions, formTemplates]);

  // Clean up errors when the form changes
  useEffect(() => {
    if (form.assigneeIds.length) {
      setErrors((prev) => ({ ...prev, assigneeIds: undefined }));
    }
    if (form.formTemplateId) {
      setErrors((prev) => ({ ...prev, formTemplateId: undefined }));
    }
  }, [form.assigneeIds, form.formTemplateId]);

  const requestTypeOptions = useMemo((): ISelectInputOption[] => {
    const disabledOptions: string[] = [];
    if (!isFeatureEnabled(FeatureFlagName.DirectBooking)) {
      disabledOptions.push(WidgetRequestType.DirectBooking);
    }
    if (!hasPrescriptionCapabilities) {
      disabledOptions.push(WidgetRequestType.AdvancedRequestRxRefill);
    }

    if (!isFeatureEnabled(FeatureFlagName.CarePlans)) {
      disabledOptions.push(WidgetRequestType.Teletriage);
    }

    if (!isFeatureEnabled(FeatureFlagName.LiveAgent)) {
      disabledOptions.push(WidgetRequestType.LiveAgent);
    }

    return widgetRequestTypeOptions.filter((o) => disabledOptions.indexOf(o.value) < 0);
  }, [hasPrescriptionCapabilities, isFeatureEnabled]);

  const { data: clinicUsersData } = useGetClinicUsersQuery({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
  });

  const onFormTemplateDataLoaded = useCallback(
    (formTemplateData: GetClinicFormTemplatesQuery) => {
      const templates = [
        { label: 'Default', value: '' },
        ...(formTemplateData?.findManyFormTemplate || [])
          .filter((template) => template.isActive || template.id === formTemplate?.id)
          .map((template) => ({
            label: template.title,
            value: template.id,
          })),
      ];
      setFormTemplates(sortBy(templates, ['label']));
    },
    [formTemplate?.id],
  );

  const { templatesLoading } = useForms({ onCompleted: onFormTemplateDataLoaded });

  const clinicUsersOptions = useMemo(() => {
    if (!clinicUsersData?.users) return [];
    return clinicUsersData.users.map((user) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.id,
      isSelected: form.assigneeIds.includes(user.id),
    }));
  }, [clinicUsersData, form.assigneeIds]);

  const handleOptionSelected = useCallback(
    (option: MenuItemProps): void => {
      trackEvent(SettingsRequestWidgetEvents.AddAssignee);
      setForm((prev) => {
        const optionValue = option.value as string;
        const assigneeIds = prev.assigneeIds.includes(optionValue)
          ? prev.assigneeIds.filter((id) => id !== optionValue)
          : [...prev.assigneeIds, optionValue];
        return { ...prev, assigneeIds };
      });
    },
    [trackEvent],
  );

  const handleDeleteAssignee = useCallback((id: string) => {
    setForm((prev) => ({ ...prev, assigneeIds: prev.assigneeIds.filter((assigneeId) => assigneeId !== id) }));
  }, []);

  const onSubmit = useCallback((): void => {
    if (!form.assigneeIds.length) {
      setErrors((prev) => ({ ...prev, assigneeIds: 'Please select at least one assignee for this request option' }));
      return;
    }
    if (form.requestType !== WidgetRequestType.DirectBooking && !form.formTemplateId) {
      setErrors((prev) => ({ ...prev, formTemplateId: 'Please select a form template' }));
      return;
    }
    trackEvent(SettingsRequestWidgetEvents.AddNewRequest);
    onSubmitProp({ ...form, requestTypeId, requestTypeAction });
  }, [form, requestTypeId, requestTypeAction, trackEvent, onSubmitProp]);

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onCancel} scrollBehavior="outside">
      <ModalHeader>{requestTypeId ? 'Edit Request Option' : 'Add Request Option'}</ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      <ModalBody>
        <Box as="form">
          <HStack>
            <Select
              name="requestType"
              label="Request Type"
              options={requestTypeOptions}
              value={form.requestType}
              listProps={{
                onSelect: (selectedItem: MenuItemProps): void => {
                  setForm((prev) => ({ ...prev, requestType: selectedItem.value as WidgetRequestType }));
                  if (selectedItem.value === WidgetRequestType.DirectBooking) {
                    setForm((prev) => ({ ...prev, formTemplateId: '' }));
                  }
                },
                isSearchable: true,
                maxH: '250px',
              }}
              labelStyle={{ fontWeight: 'bold' }}
              onBlur={(): void => {
                trackEvent(SettingsRequestWidgetEvents.SelectRequestType);
              }}
              isRequired
            />
            <TextInput
              name="displayName"
              label="Alternative Request Name"
              value={form.displayName}
              onChange={(e): void => {
                e.persist();
                setForm((prev) => ({ ...prev, displayName: e.target?.value }));
              }}
              labelStyle={{ fontWeight: 'bold' }}
              onBlur={(): void => {
                trackEvent(SettingsRequestWidgetEvents.AddDisplayName);
              }}
            />
          </HStack>

          <HStack mt={5} alignItems="flex-start">
            {isFeatureEnabled(FeatureFlagName.DisplayWidgetRequestVisibility) && (
              <RadioGroup
                name="requestVisibility"
                radios={WidgetVisibilityOptions}
                direction="column"
                spacing={2}
                value={form.requestVisibility}
                onChange={(v: string): void =>
                  setForm((prev) => ({ ...prev, requestVisibility: v as WidgetVisibility }))
                }
                label={
                  <Text size="md" fontWeight="bold" mb={2}>
                    Display This Request:
                  </Text>
                }
                labelStyle={{ fontWeight: 'bold' }}
                w="100%"
                onBlur={(): void => {
                  trackEvent(SettingsRequestWidgetEvents.SelectRequestVisibility);
                }}
              />
            )}
            <VStack w="100%" align="flex-start">
              <Text size="md" fontWeight="bold">
                Button Preview
              </Text>
              {form.requestType === WidgetRequestType.InboundSms ? (
                <Alert>The button for this request type is hidden from the Website Widget</Alert>
              ) : (
                <Button
                  variant="ghostNeutral"
                  iconName={WidgetRequestTypeIconNames[form.requestType]}
                  boxShadow="md"
                  mt={0}
                >
                  {form.displayName || WidgetRequestTypeLabels[form.requestType]}
                </Button>
              )}
            </VStack>
          </HStack>

          {form.requestType !== WidgetRequestType.DirectBooking && (
            <VStack align="flex-start" mt={5}>
              <HStack align="flex-end" w="100%">
                {templatesLoading ? (
                  <LoadingSpinner message="Loading forms..." direction="row" />
                ) : (
                  <>
                    <Select
                      name="formTemplate"
                      label="Request Form"
                      options={formTemplates}
                      value={form.formTemplateId}
                      listProps={{
                        onSelect: (selectedItem: MenuItemProps): void => {
                          setForm((prev) => ({ ...prev, formTemplateId: selectedItem.value as WidgetRequestType }));
                        },
                        maxH: '250px',
                      }}
                      labelStyle={{ fontWeight: 'bold' }}
                      isInvalid={!!errors.formTemplateId}
                      errorText={errors.formTemplateId}
                      onBlur={(): void => {
                        trackEvent(SettingsRequestWidgetEvents.AddForm);
                      }}
                      isRequired
                    />
                    {form.formTemplateId && (
                      <Button variant="secondary">
                        <Link to={`/forms/${form.formTemplateId}`}>Edit Form</Link>
                      </Button>
                    )}
                  </>
                )}
              </HStack>
            </VStack>
          )}

          <HStack alignItems="flex-start" mt={5}>
            <Select
              name="channelStatusId"
              label="Default Conversation Status"
              options={channelStatusOptions}
              value={form.channelStatusId}
              listProps={{
                onSelect: (selectedItem: MenuItemProps): void => {
                  setForm((prev) => ({ ...prev, channelStatusId: selectedItem.value as string }));
                },
                isSearchable: true,
                maxH: '250px',
              }}
              labelStyle={{ fontWeight: 'bold' }}
              w="100%"
              onBlur={(): void => {
                trackEvent(SettingsRequestWidgetEvents.AddConversationStatus);
              }}
              isRequired
            />
            <VStack w="100%" align="flex-start">
              <Text as="label" fontWeight="bold" mb={0}>
                Assigned To
                <Text as="span" variant="danger" fontWeight="bold" ml={1}>
                  *
                </Text>
              </Text>
              <Menu
                buttonProps={{
                  variant: 'secondarySubtle',
                  rightIconName: 'chevronDown',
                  size: 'md',
                  text: 'Please Select Assignee(s)',
                }}
                listProps={{
                  menuItems: clinicUsersOptions,
                  isSearchable: true,
                  isMultiSelect: true,
                  canSelectAll: true,
                  onSelect: handleOptionSelected,
                  maxH: '250px',
                }}
              />
              {errors.assigneeIds && (
                <Text variant="danger" size="sm">
                  {errors.assigneeIds}
                </Text>
              )}
              <Wrap>
                {form.assigneeIds.map((id) => {
                  const selectedAssignee = clinicUsersOptions.find((option) => option.value === id);
                  return (
                    <Tag
                      key={id}
                      label={selectedAssignee?.label || ''}
                      rightElement={
                        <Icon name="close" size="sm" onClick={(): void => handleDeleteAssignee(id)} cursor="pointer" />
                      }
                    />
                  );
                })}
              </Wrap>
            </VStack>
          </HStack>
        </Box>
      </ModalBody>
      <ModalFooterButtons onCancel={onCancel} onSubmit={onSubmit} isLoading={isLoading} submitText="Save" />
    </Modal>
  );
};

export default RequestTypesModal;
